import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, ClockCountdown, FilmReel, PlayCircle, Users } from '@phosphor-icons/react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatRelative, pct } from '../lib/format';
import {
  Avatar,
  Card,
  ContentStatusPill,
  EmptyState,
  ErrorState,
  LoadingState,
  ProgressBar,
  SectionHeading,
  StatCard,
} from '../components/ui';

export function StudioOverview() {
  const s = useAsync(() => api.admin.overview(), []);

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  const { kpis, pipeline, gradingQueue } = s.data;

  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="Studio"
        title="Instructor overview"
        description="A snapshot of your members, content, and what needs your attention."
      />

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard icon={Users} tone="teal" label="Active members" value={kpis.activeMembers} hint="With active access" />
        <StatCard
          icon={ClockCountdown}
          tone="amber"
          label="Pending members"
          value={kpis.awaitingPayment}
          hint="Awaiting your approval"
        />
        <StatCard icon={CheckCircle} tone="violet" label="Completion" value={pct(kpis.completionRate)} hint="Average across members" />
        <StatCard icon={PlayCircle} tone="blue" label="Watch hours" value={`${kpis.watchHours}h`} hint="Total this month" />
        <StatCard icon={FilmReel} tone="rose" label="Pending reviews" value={kpis.pendingReviews} hint="Labs to grade" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Content pipeline snapshot */}
        <Card className="p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-bold">Content pipeline</h3>
            <Link to="/studio/content" className="btn-ghost -mr-2 text-sm">
              Manage content <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
          {pipeline.length > 0 ? (
            <ul className="space-y-4">
              {pipeline.map((row) => {
                const recordedPct = row.total === 0 ? 0 : Math.round((row.recorded / row.total) * 100);
                return (
                  <li key={row.id}>
                    <div className="mb-1.5 flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-semibold text-brand-navy">{row.title}</span>
                      </div>
                      <ContentStatusPill status={row.status} />
                    </div>
                    <div className="flex items-center gap-3">
                      <ProgressBar value={row.recorded} max={row.total} className="flex-1" />
                      <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-500">
                        {row.recorded}/{row.total}
                        <span className="ml-1 text-slate-400">({recordedPct}%)</span>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState
              icon={FilmReel}
              title="No content yet"
              description="Modules you plan and record will show up here."
            />
          )}
        </Card>

        {/* Grading queue snapshot */}
        <Card className="flex flex-col p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-bold">Grading queue</h3>
            {gradingQueue.length > 0 && (
              <span className="pill bg-amber-50 text-amber-700">{gradingQueue.length}</span>
            )}
          </div>
          {gradingQueue.length > 0 ? (
            <>
              <ul className="flex-1 space-y-3">
                {gradingQueue.map((row) => (
                  <li key={row.id} className="flex items-center gap-3">
                    <Avatar name={row.userName} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-brand-navy">{row.userName}</div>
                      <div className="truncate text-xs text-slate-500">{row.labTitle}</div>
                    </div>
                    <span className="shrink-0 text-xs text-slate-400">{formatRelative(row.submittedAt)}</span>
                  </li>
                ))}
              </ul>
              <Link to="/studio/grading" className="btn-primary mt-5 w-full justify-center">
                Open grading queue <ArrowRight size={18} weight="bold" />
              </Link>
            </>
          ) : (
            <EmptyState
              icon={CheckCircle}
              title="All caught up"
              description="No submissions are waiting for review."
            />
          )}
        </Card>
      </div>
    </div>
  );
}
